/**
 * The three ways a customer can engage us, and what sits inside each.
 *
 * These are deliberately framed as capabilities we hold, not as claims
 * about work already delivered — /work is the only place on the site that
 * makes delivery claims, and everything there is verifiable.
 */

import type { SpineKey } from "./site";

export interface Pillar {
  key: "consult" | "build" | "product";
  /** Two-word label used on the spine diagram. */
  short: string;
  title: string;
  /** The engagement in one sentence, written for a business reader. */
  lede: string;
  /** Which stages of Think → Design → Build → Deploy → Operate this covers. */
  covers: SpineKey[];
  capabilities: string[];
  href: string;
  cta: string;
}

export const PILLARS: Pillar[] = [
  {
    key: "consult",
    short: "Consult",
    title: "AI & IT Consulting",
    lede: "Work out where technology creates a real advantage — and, just as often, where it does not.",
    covers: ["think", "design"],
    capabilities: [
      "AI strategy",
      "Generative AI and agent design",
      "AI/ML architecture",
      "Cloud architecture",
      "Data platforms",
      "Automation assessment",
      "Digital transformation",
      "Enterprise technology advisory",
    ],
    href: "/consulting",
    cta: "Talk to an expert",
  },
  {
    key: "build",
    short: "Build",
    title: "Software Development",
    lede: "Take a complex idea from architecture through to a system carrying real load, real users and real money.",
    covers: ["design", "build", "deploy"],
    capabilities: [
      "Custom software",
      "SaaS platforms",
      "Web applications",
      "Mobile applications",
      "AI-powered applications",
      "Backend and APIs",
      "Multi-tenant systems",
      "Cloud-native infrastructure",
      "System integrations",
      "MVP to production",
    ],
    href: "/software-development",
    cta: "Scope a build",
  },
  {
    key: "product",
    short: "Product",
    title: "Proprietary Products",
    lede: "Software we designed, built, shipped and still operate — which is why we know what the last two stages actually cost.",
    covers: ["think", "design", "build", "deploy", "operate"],
    capabilities: [
      "Agent Labista — AI sales agent for real estate",
      "Hazri Book — verified workforce attendance",
    ],
    href: "/products",
    cta: "Explore the products",
  },
];

/**
 * Engineering surface area, grouped by where it sits in a real system
 * rather than listed as a wall of logos. The grouping is the point: it
 * shows we think in architectures, not in technology names.
 */
export interface CapabilityLayer {
  layer: string;
  note: string;
  items: string[];
}

export const ENGINEERING_LAYERS: CapabilityLayer[] = [
  {
    layer: "Intelligence",
    note: "Where a model earns its place in the architecture",
    items: [
      "LLM applications",
      "AI agents",
      "Retrieval-augmented generation",
      "Evaluation and guardrails",
      "Machine learning",
      "Document understanding",
    ],
  },
  {
    layer: "Product surface",
    note: "What the customer's customer actually touches",
    items: [
      "React and TypeScript",
      "Mobile applications",
      "Conversational interfaces",
      "WhatsApp and messaging channels",
      "Offline-first clients",
      "Accessibility and i18n",
    ],
  },
  {
    layer: "Core systems",
    note: "The part that has to be right at three in the morning",
    items: [
      "Backend services and APIs",
      "Multi-tenant data isolation",
      "Relational data modelling",
      "Background jobs and queues",
      "Integrations",
      "Audit and evidence trails",
    ],
  },
  {
    layer: "Platform",
    note: "How it gets built, shipped and watched",
    items: [
      "Cloud infrastructure",
      "CI/CD",
      "Observability",
      "Data residency",
      "Access control",
      "Cost control",
    ],
  },
];

/** Why us. Specific differentiators — no "quality", no "customer satisfaction". */
export const DIFFERENTIATORS = [
  {
    title: "Business first",
    body: "We start with the problem and the money attached to it. The technology decision comes second, and sometimes the answer is that you do not need the technology.",
  },
  {
    title: "Engineering depth",
    body: "Multi-tenant isolation, offline sync, audit trails, data residency. The unglamorous parts that decide whether a system survives its second year.",
  },
  {
    title: "AI-native, not AI-decorated",
    body: "A model goes into the architecture where it creates leverage that conventional code cannot. Everywhere else we write ordinary, boring, reliable software.",
  },
  {
    title: "End to end",
    body: "Strategy, architecture, engineering, deployment, operation. We can take any part of that line, and we can take all of it.",
  },
  {
    title: "Product mindset",
    body: "We run our own SaaS in production, in regulated markets, for paying customers. We have been on the wrong end of our own architecture decisions and it changes how we make them.",
  },
  {
    title: "Built for more than one market",
    body: "Arabic and right-to-left, Gulf labour rules, Indian payment and compliance regimes, data residency, eight-language conversational AI. Already built, already in production.",
  },
] as const;

/** How we work. Five stages, mapped onto the same spine as everything else. */
export const PROCESS = [
  {
    n: "01",
    title: "Discover",
    body: "Understand the business problem, the constraint behind it, and the outcome that would count as success. Written down, agreed, and short.",
  },
  {
    n: "02",
    title: "Architect",
    body: "Design the system — data model, service boundaries, where AI belongs, where it does not, what it runs on and what it costs.",
  },
  {
    n: "03",
    title: "Build",
    body: "Engineer it. Tests, review, CI, observability from the first commit rather than bolted on before launch.",
  },
  {
    n: "04",
    title: "Launch",
    body: "Deploy, integrate with the systems already in place, and put real users on it. The first week in production teaches more than the previous month.",
  },
  {
    n: "05",
    title: "Evolve",
    body: "Watch it under load, fix what the real world found, and keep shipping. Or hand it over cleanly, documented, to your own team.",
  },
] as const;
